import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Link2, Globe, Key, Plus, X, RefreshCw, Check, AlertCircle,
  Activity, BarChart3, Settings, Trash2, Play, ExternalLink,
  Wifi, WifiOff, Bot, ChevronRight, Copy, Code, Database, Search, ChevronDown, ChevronUp,
  CreditCard, Users, Crown, Shield, Star, Zap, Home, Webhook, Brain, BookOpen
} from "lucide-react";
import StripeAdminPanel from "./StripeAdminPanel";
import HomebridgeAdminPanel from "./HomebridgeAdminPanel";
import FishAudioAdminPanel from "./FishAudioAdminPanel";
import OkbotAdminPanel from "./OkbotAdminPanel";
import PlatformSyncManager from "./PlatformSyncManager";
import AIDiagnosticsPanel from "./AIDiagnosticsPanel";
import MasterBrainPanel from "./MasterBrainPanel";
import ConnectAPIPanel from "./ConnectAPIPanel";
import OkPosConnectionPanel from "./OkPosConnectionPanel";

const METHOD_COLORS = {
  GET: "bg-green-500/20 text-green-300",
  POST: "bg-blue-500/20 text-blue-300",
  PUT: "bg-yellow-500/20 text-yellow-300",
  DELETE: "bg-red-500/20 text-red-300",
  PATCH: "bg-purple-500/20 text-purple-300",
};

function SystemAPIsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [expandedTag, setExpandedTag] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/system-endpoints`)
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copiado"); };

  if (loading) return <div className="flex justify-center py-12"><RefreshCw size={20} className="animate-spin text-gray-500" /></div>;
  if (!data) return null;

  const filtered = (data.endpoints || []).filter(ep =>
    !search ||
    ep.path.toLowerCase().includes(search.toLowerCase()) ||
    ep.method.toLowerCase().includes(search.toLowerCase()) ||
    ep.summary.toLowerCase().includes(search.toLowerCase())
  );

  const grouped = {};
  filtered.forEach(ep => {
    const tag = ep.tags[0] || "general";
    if (!grouped[tag]) grouped[tag] = [];
    grouped[tag].push(ep);
  });

  return (
    <div className="space-y-4">
      <div className="glass-panel p-4 bg-blue-500/5 border-blue-500/20">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
          <div>
            <p className="text-2xl font-bold text-white">{data.total}</p>
            <p className="text-gray-500 text-xs">Endpoints totales</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-green-400">{(data.endpoints || []).filter(e => e.method === "GET").length}</p>
            <p className="text-gray-500 text-xs">GET</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-blue-400">{(data.endpoints || []).filter(e => e.method === "POST").length}</p>
            <p className="text-gray-500 text-xs">POST</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-red-400">{Object.keys(data.grouped || {}).length}</p>
            <p className="text-gray-500 text-xs">Módulos</p>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Buscar endpoints..."
            className="input-glass w-full pl-9 text-sm"
          />
        </div>
        <div className="flex gap-2">
          <button onClick={() => copy(data.base_url)} className="btn-secondary text-xs flex items-center gap-1.5">
            <Copy size={12} />Base URL
          </button>
          <a href={data.docs_url} target="_blank" rel="noopener noreferrer" className="btn-secondary text-xs flex items-center gap-1.5">
            <ExternalLink size={12} />Swagger
          </a>
        </div>
      </div>

      <div className="space-y-2">
        {Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([tag, eps]) => (
          <div key={tag} className="glass-panel overflow-hidden">
            <button
              onClick={() => setExpandedTag(expandedTag === tag ? null : tag)}
              className="w-full flex items-center justify-between p-3.5 hover:bg-white/3 transition-colors"
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-white">{tag}</span>
                <span className="text-gray-600 text-xs">{eps.length} endpoint{eps.length !== 1 ? "s" : ""}</span>
              </div>
              {expandedTag === tag ? <ChevronUp size={13} className="text-gray-500" /> : <ChevronDown size={13} className="text-gray-500" />}
            </button>

            {(expandedTag === tag || search) && (
              <div className="border-t border-white/5 divide-y divide-white/3">
                {eps.map((ep, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 hover:bg-white/3 transition-colors group">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded font-mono flex-shrink-0 ${METHOD_COLORS[ep.method] || "bg-gray-500/20 text-gray-300"}`}>
                      {ep.method}
                    </span>
                    <code className="text-gray-300 text-xs flex-1 min-w-0 truncate font-mono">{ep.path}</code>
                    <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                      <button onClick={() => copy(ep.path)} className="p-1 text-gray-500 hover:text-white hover:bg-white/10 rounded text-xs" title="Copiar path">
                        <Copy size={11} />
                      </button>
                      <button onClick={() => copy(ep.full_url)} className="p-1 text-gray-500 hover:text-white hover:bg-white/10 rounded text-xs" title="Copiar URL">
                        <Globe size={11} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const isOkPosPlatform = (platform) => {
  if (platform.platform_id === "okpos" || platform.platform_code?.toLowerCase() === "okpos") return true;
  try {
    return new URL(platform.platform_url).hostname.toLowerCase().split(".")
      .some(label => label === "okpos" || label.startsWith("okpos-"));
  } catch {
    return false;
  }
};

const PLAN_META = {
  free:     { label: "Gratuito",  color: "text-gray-400",   bg: "bg-gray-500/20",   border: "border-gray-500/30",   icon: Shield },
  esencial: { label: "Esencial",  color: "text-blue-400",   bg: "bg-blue-500/20",   border: "border-blue-500/30",   icon: Zap },
  pro:      { label: "Pro",       color: "text-purple-400", bg: "bg-purple-500/20", border: "border-purple-500/30", icon: Star },
  elite:    { label: "Elite",     color: "text-amber-400",  bg: "bg-amber-500/20",  border: "border-amber-500/30",  icon: Crown },
};

function UsersPlanesPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [saving, setSaving] = useState({});
  const [bulkPlan, setBulkPlan] = useState("free");
  const [selected, setSelected] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/users/plans-summary`);
      setData(r.data);
    } catch { toast.error("Error cargando usuarios"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const assignPlan = async (userId, plan) => {
    setSaving(s => ({ ...s, [userId]: true }));
    try {
      await axios.put(`${API}/admin/users/${userId}/plan`, { plan });
      toast.success(`Plan ${PLAN_META[plan]?.label || plan} asignado`);
      setData(d => ({
        ...d,
        users: d.users.map(u => u.id === userId ? { ...u, okbot_plan: plan } : u)
      }));
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSaving(s => ({ ...s, [userId]: false })); }
  };

  const bulkAssign = async () => {
    if (!selected.length) { toast.error("Selecciona usuarios primero"); return; }
    try {
      await axios.put(`${API}/admin/users/bulk-plan`, { user_ids: selected, plan: bulkPlan });
      toast.success(`${selected.length} usuario(s) → ${PLAN_META[bulkPlan]?.label}`);
      setSelected([]);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const toggleSelect = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const selectAll = () => {
    const visible = filtered.map(u => u.id);
    setSelected(s => s.length === visible.length ? [] : visible);
  };

  if (loading) return <div className="flex justify-center py-16"><RefreshCw size={22} className="animate-spin text-gray-500" /></div>;
  if (!data) return null;

  const filtered = (data.users || []).filter(u => {
    const matchSearch = !search || (u.okuser || "").includes(search.toLowerCase()) || (u.name || "").toLowerCase().includes(search.toLowerCase()) || (u.email || "").includes(search.toLowerCase());
    const matchFilter = filter === "all" || (u.okbot_plan || "free") === filter;
    return matchSearch && matchFilter;
  });

  return (
    <div className="space-y-5">
      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Object.entries(PLAN_META).map(([key, meta]) => {
          const Icon = meta.icon;
          const count = data.plan_counts?.[key] || 0;
          return (
            <div key={key} className={`glass-panel p-4 border ${meta.border} cursor-pointer ${filter === key ? 'ring-2 ring-white/20' : ''}`} onClick={() => setFilter(f => f === key ? "all" : key)}>
              <div className="flex items-center gap-2 mb-2">
                <Icon size={16} className={meta.color} />
                <span className={`text-sm font-medium ${meta.color}`}>{meta.label}</span>
              </div>
              <p className="text-2xl font-bold text-white">{count}</p>
              <p className="text-gray-500 text-xs">usuarios</p>
            </div>
          );
        })}
      </div>

      {/* Toolbar */}
      <div className="glass-panel p-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[180px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar usuario..."
            className="input-glass w-full pl-9 text-sm" />
        </div>
        <select value={filter} onChange={e => setFilter(e.target.value)} className="input-glass text-sm">
          <option value="all">Todos los planes</option>
          {Object.entries(PLAN_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
        </select>
        <button onClick={load} className="p-2 bg-white/5 hover:bg-white/10 rounded-lg transition-colors">
          <RefreshCw size={15} className="text-gray-400" />
        </button>
        {/* Bulk action */}
        {selected.length > 0 && (
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-gray-400 text-sm">{selected.length} seleccionados</span>
            <select value={bulkPlan} onChange={e => setBulkPlan(e.target.value)} className="input-glass text-sm">
              {Object.entries(PLAN_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
            </select>
            <button onClick={bulkAssign} className="btn-primary text-sm px-3 py-1.5">Asignar</button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="glass-panel overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-white/10">
              <tr className="text-gray-500 text-xs">
                <th className="p-3 text-left w-8">
                  <input type="checkbox" className="rounded" checked={selected.length === filtered.length && filtered.length > 0}
                    onChange={selectAll} />
                </th>
                <th className="p-3 text-left">Usuario</th>
                <th className="p-3 text-left hidden md:table-cell">Email</th>
                <th className="p-3 text-center">Plan actual</th>
                <th className="p-3 text-center">Cambiar plan</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filtered.length === 0 && (
                <tr><td colSpan={5} className="text-center text-gray-500 py-10">Sin usuarios</td></tr>
              )}
              {filtered.map(user => {
                const plan = user.okbot_plan || "free";
                const meta = PLAN_META[plan] || PLAN_META.free;
                const Icon = meta.icon;
                return (
                  <tr key={user.id} className="hover:bg-white/3 transition-colors">
                    <td className="p-3">
                      <input type="checkbox" className="rounded" checked={selected.includes(user.id)}
                        onChange={() => toggleSelect(user.id)} />
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-red-500/30 to-purple-500/30 flex items-center justify-center text-xs font-bold text-white">
                          {(user.first_name || user.okuser || "?")[0].toUpperCase()}
                        </div>
                        <div>
                          <p className="text-white font-medium text-sm">{user.name || user.okuser}</p>
                          <p className="text-gray-500 text-xs">@{user.okuser}</p>
                        </div>
                        {user.is_locked && <span className="text-red-400 text-xs ml-1">🔒</span>}
                      </div>
                    </td>
                    <td className="p-3 hidden md:table-cell text-gray-400 text-xs">{user.email || "—"}</td>
                    <td className="p-3 text-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${meta.bg} ${meta.color} border ${meta.border}`}>
                        <Icon size={10} />
                        {meta.label}
                      </span>
                    </td>
                    <td className="p-3 text-center">
                      <select
                        value={plan}
                        onChange={e => assignPlan(user.id, e.target.value)}
                        disabled={saving[user.id]}
                        className="input-glass text-xs py-1 px-2 w-32"
                      >
                        {Object.entries(PLAN_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="p-3 border-t border-white/10 text-gray-500 text-xs text-right">
          {filtered.length} de {data.total} usuarios · Total: {data.total}
        </div>
      </div>

      {/* Info block */}
      <div className="glass-panel p-4 bg-blue-500/5 border-blue-500/20">
        <p className="text-blue-300 text-sm font-medium mb-2">📋 Sobre los planes</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(PLAN_META).map(([k, m]) => (
            <div key={k} className="text-xs text-gray-400">
              <span className={`font-medium ${m.color}`}>{m.label}</span>
              <span> — {data.plans?.[k]?.description || ""}</span>
            </div>
          ))}
        </div>
        <p className="text-gray-500 text-xs mt-2">Los nuevos usuarios se registran automáticamente con el plan <strong className="text-gray-400">Gratuito</strong>.</p>
      </div>
    </div>
  );
}

const SuperAdminConnect = () => {
  const [platformCode, setPlatformCode] = useState("");
  const [platforms, setPlatforms] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [activeSection, setActiveSection] = useState("usuarios");
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkForm, setLinkForm] = useState({ platform_url: "", platform_code: "", api_key: "" });
  const [linking, setLinking] = useState(false);
  const [discoveredEndpoints, setDiscoveredEndpoints] = useState([]);
  const [selectedEndpoints, setSelectedEndpoints] = useState([]);
  const [linkStep, setLinkStep] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [codeRes, platformsRes, statusRes] = await Promise.all([
        axios.get(`${API}/admin/connect/platform-code`),
        axios.get(`${API}/admin/connect/platforms`),
        axios.get(`${API}/admin/connect/connection-status`)
      ]);
      setPlatformCode(codeRes.data.platform_code);
      setPlatforms(platformsRes.data);
      setConnectionStatus(statusRes.data);
    } catch (e) {
      console.error("Error loading connect data:", e);
    } finally {
      setLoading(false);
    }
  };

  const handleDiscoverEndpoints = async () => {
    if (!linkForm.platform_url || !linkForm.platform_code || !linkForm.api_key) {
      toast.error("Complete todos los campos");
      return;
    }
    setLinking(true);
    try {
      const resp = await axios.post(`${API}/admin/connect/ai-connection-agent`, {
        action: "discover_endpoints",
        platform_url: linkForm.platform_url,
        api_key: linkForm.api_key
      });
      setDiscoveredEndpoints(resp.data.endpoints || []);
      setSelectedEndpoints(resp.data.endpoints.filter(e => e.available).map(e => e.path));
      setLinkStep(2);
      toast.success(`${resp.data.available} endpoints encontrados`);
    } catch (e) {
      toast.error("Error al conectar con la plataforma");
    } finally {
      setLinking(false);
    }
  };

  const handleFinalConnect = async () => {
    setLinking(true);
    try {
      await axios.post(`${API}/admin/connect/link-platform`, linkForm);
      toast.success("Plataforma conectada exitosamente");
      setShowLinkModal(false);
      setLinkForm({ platform_url: "", platform_code: "", api_key: "" });
      setLinkStep(1);
      setDiscoveredEndpoints([]);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al conectar");
    } finally {
      setLinking(false);
    }
  };

  const handleDisconnect = async (platformId) => {
    if (!window.confirm("¿Desconectar esta plataforma?")) return;
    try {
      await axios.delete(`${API}/admin/connect/platforms/${platformId}`);
      toast.success("Plataforma desconectada");
      loadData();
    } catch (e) {
      toast.error("Error al desconectar");
    }
  };

  const handleTestConnection = async (platformId, endpoint) => {
    try {
      const resp = await axios.post(`${API}/admin/connect/platforms/${platformId}/test`, {
        platform_id: platformId,
        endpoint: endpoint
      });
      if (resp.data.success) {
        toast.success(`Conexión exitosa (${resp.data.latency_ms}ms)`);
      } else {
        toast.error(`Error: ${resp.data.error || resp.data.status_code}`);
      }
    } catch (e) {
      toast.error("Error de conexión");
    }
  };

  const copyPlatformCode = () => {
    navigator.clipboard.writeText(platformCode);
    toast.success("Código copiado");
  };

  const sections = [
    { id: "brain",      label: "Cerebro OkBot",          icon: Brain },
    { id: "connectapi", label: "Connect API",             icon: Code },
    { id: "diagnostics",label: "Diagnóstico IA",         icon: Bot },
    { id: "sync",      label: "Sync Manager",            icon: Webhook },
    { id: "usuarios",  label: "Usuarios & Planes",        icon: Users },
    { id: "platforms", label: "Plataformas Conectadas",   icon: Link2 },
    { id: "apis",      label: "APIs del Sistema",         icon: Database },
    { id: "agent",     label: "Agente IA de Conexión",    icon: Bot },
    { id: "status",    label: "Panel de Estado",          icon: Activity },
    { id: "stripe",    label: "Stripe Billing",           icon: CreditCard },
    { id: "homebridge",label: "OkHome · Homebridge",      icon: Home },
    { id: "fishaudio", label: "🐟 Fish Audio",            icon: Zap },
    { id: "okbot",     label: "📚 OkBot · OkBook",       icon: BookOpen },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-red-400 animate-spin" />
      </div>
    );
  }

  return (
    <div>
      {/* Header con Código de Plataforma */}
      <div className="glass-panel p-6 mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-white mb-1">Super Admin Connect</h2>
            <p className="text-gray-500 text-sm">Conecta OkBot con otras plataformas</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="bg-black/40 rounded-xl px-4 py-3 flex items-center gap-3">
              <span className="text-gray-400 text-sm">Tu Código:</span>
              <code className="text-red-400 font-mono font-bold text-lg">{platformCode}</code>
              <button onClick={copyPlatformCode} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                <Copy size={16} className="text-gray-400" />
              </button>
            </div>
            <button onClick={() => setShowLinkModal(true)} className="btn-primary flex items-center gap-2">
              <Plus size={16} />Vincular Plataforma
            </button>
          </div>
        </div>
      </div>

      {/* Section Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {sections.map(s => {
          const Icon = s.icon;
          return (
            <button
              key={s.id}
              onClick={() => setActiveSection(s.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-colors whitespace-nowrap ${
                activeSection === s.id ? 'bg-red-500 text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
            >
              <Icon size={16} />{s.label}
            </button>
          );
        })}
      </div>

      {/* Cerebro OkBot — Master Brain */}
      {activeSection === "brain" && (
        <MasterBrainPanel />
      )}

      {/* Connect API — OkBot como cerebro para plataformas externas */}
      {activeSection === "connectapi" && (
        <ConnectAPIPanel platforms={platforms} />
      )}

      {/* Diagnóstico IA — Infrastructure Analyzer */}
      {activeSection === "diagnostics" && (
        <AIDiagnosticsPanel />
      )}

      {/* Sync Manager — Reverse Configuration Engine */}
      {activeSection === "sync" && (
        <PlatformSyncManager platforms={platforms} />
      )}

      {/* Usuarios & Planes */}
      {activeSection === "usuarios" && <UsersPlanesPanel />}

      {/* Plataformas Conectadas */}
      {activeSection === "platforms" && (
        <div className="space-y-4">
          <OkPosConnectionPanel />
          {platforms.length === 0 ? (
            <div className="glass-panel p-12 text-center">
              <Globe className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 mb-2">No hay plataformas conectadas</p>
              <p className="text-gray-600 text-sm">Vincula tu primera plataforma para comenzar</p>
            </div>
          ) : (
            platforms.map(platform => (
              <div key={platform.id} className="glass-panel p-5">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      platform.status === 'active' ? 'bg-green-500/20' : 'bg-red-500/20'
                    }`}>
                      {platform.status === 'active' ? 
                        <Wifi className="text-green-400" size={24} /> : 
                        <WifiOff className="text-red-400" size={24} />
                      }
                    </div>
                    <div>
                      <code className="text-red-400 font-mono font-bold">{platform.platform_code}</code>
                      <p className="text-gray-400 text-sm mt-1">{platform.platform_url}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`badge ${isOkPosPlatform(platform) ? 'badge-warning' : platform.status === 'active' ? 'badge-success' : 'badge-error'}`}>
                      {isOkPosPlatform(platform) ? 'Registro general' : platform.status === 'active' ? 'Activa' : 'Inactiva'}
                    </span>
                    <button onClick={() => handleDisconnect(platform.id)} className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <div className="bg-black/20 rounded-lg p-3">
                    <p className="text-gray-500 text-xs">Endpoints</p>
                    <p className="text-white font-semibold">{platform.endpoints_enabled?.length || 0}</p>
                  </div>
                  <div className="bg-black/20 rounded-lg p-3">
                    <p className="text-gray-500 text-xs">Requests Totales</p>
                    <p className="text-white font-semibold">{platform.connection_stats?.total_requests || 0}</p>
                  </div>
                  <div className="bg-black/20 rounded-lg p-3">
                    <p className="text-gray-500 text-xs">Exitosos</p>
                    <p className="text-green-400 font-semibold">{platform.connection_stats?.successful_requests || 0}</p>
                  </div>
                  <div className="bg-black/20 rounded-lg p-3">
                    <p className="text-gray-500 text-xs">OAuth SSO</p>
                    <p className="text-yellow-400 font-semibold capitalize">{platform.oauth_sso_status || 'Pendiente'}</p>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button 
                    onClick={() => handleTestConnection(platform.id, '/api/health')}
                    className="btn-secondary text-sm flex items-center gap-2"
                  >
                    <Play size={14} />Test Conexión
                  </button>
                  <a href={platform.platform_url} target="_blank" rel="noopener noreferrer" 
                     className="btn-secondary text-sm flex items-center gap-2">
                    <ExternalLink size={14} />Visitar
                  </a>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* APIs del Sistema */}
      {activeSection === "apis" && (
        <SystemAPIsPanel />
      )}

      {/* Agente IA de Conexión */}
      {activeSection === "agent" && (
        <div className="glass-panel p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-500/20 to-purple-500/20 flex items-center justify-center">
              <Bot className="text-red-400" size={24} />
            </div>
            <div>
              <h3 className="text-white font-semibold">Agente IA de Conexión</h3>
              <p className="text-gray-500 text-sm">Automatiza la conexión y vinculación entre plataformas</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-black/20 rounded-xl p-4">
              <h4 className="text-white font-medium mb-2">Capacidades del Agente</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li className="flex items-center gap-2"><Check size={14} className="text-green-400" />Descubrimiento automático de endpoints</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-green-400" />Generación de webhooks necesarios</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-green-400" />Pruebas de OAuth SSO</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-green-400" />Detección de errores de conexión</li>
                <li className="flex items-center gap-2"><Check size={14} className="text-green-400" />Comunicación con agentes de otras plataformas</li>
              </ul>
            </div>

            <div className="bg-black/20 rounded-xl p-4">
              <h4 className="text-white font-medium mb-3">Acciones Rápidas</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {platforms.map(p => (
                  <button 
                    key={p.id}
                    onClick={() => handleTestConnection(p.id, '/api/health')}
                    className="bg-white/5 hover:bg-white/10 rounded-lg p-3 text-left transition-colors"
                  >
                    <p className="text-white font-medium text-sm">{p.platform_code}</p>
                    <p className="text-gray-500 text-xs">Verificar conexión</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Panel de Estado */}
      {activeSection === "status" && connectionStatus && (
        <div className="space-y-6">
          <OkPosConnectionPanel />
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="glass-panel p-5">
              <p className="text-gray-500 text-sm mb-1">Plataformas Totales</p>
              <p className="text-3xl font-bold text-white">{connectionStatus.summary.total_platforms}</p>
            </div>
            <div className="glass-panel p-5">
              <p className="text-gray-500 text-sm mb-1">Activas</p>
              <p className="text-3xl font-bold text-green-400">{connectionStatus.summary.active_platforms}</p>
            </div>
            <div className="glass-panel p-5">
              <p className="text-gray-500 text-sm mb-1">Requests Totales</p>
              <p className="text-3xl font-bold text-white">{connectionStatus.summary.total_requests}</p>
            </div>
            <div className="glass-panel p-5">
              <p className="text-gray-500 text-sm mb-1">Tasa de Éxito</p>
              <p className="text-3xl font-bold text-red-400">{connectionStatus.summary.success_rate}%</p>
            </div>
          </div>

          {/* Platforms Status */}
          <div className="glass-panel p-6">
            <h3 className="text-white font-semibold mb-4">Estado por Plataforma</h3>
            <div className="space-y-3">
              {connectionStatus.platforms.map(p => (
                <div key={p.id} className="flex items-center justify-between bg-black/20 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${p.status === 'active' ? 'bg-green-400' : 'bg-red-400'}`} />
                    <code className="text-gray-300 font-mono">{p.platform_code}</code>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-gray-400">{p.endpoints_count} endpoints</span>
                    <span className="text-gray-400">{p.stats?.total_requests || 0} requests</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Incidents */}
          <div className="glass-panel p-6">
            <h3 className="text-white font-semibold mb-4">Incidencias Recientes</h3>
            {connectionStatus.recent_incidents.length === 0 ? (
              <p className="text-gray-500 text-center py-4">Sin incidencias registradas</p>
            ) : (
              <div className="space-y-2">
                {connectionStatus.recent_incidents.slice(0, 10).map((incident, idx) => (
                  <div key={idx} className="flex items-center gap-3 bg-red-500/10 rounded-lg p-3">
                    <AlertCircle size={16} className="text-red-400" />
                    <span className="text-gray-300 text-sm">{incident.platform_code}: {incident.message || 'Error de conexión'}</span>
                    <span className="text-gray-500 text-xs ml-auto">{incident.timestamp}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Stripe Billing */}
      {activeSection === "stripe" && (
        <StripeAdminPanel />
      )}

      {/* OkHome · Homebridge */}
      {activeSection === "homebridge" && (
        <HomebridgeAdminPanel />
      )}

      {/* Fish Audio */}
      {activeSection === "fishaudio" && (
        <FishAudioAdminPanel />
      )}

      {/* OkBot · OkBook */}
      {activeSection === "okbot" && (
        <OkbotAdminPanel />
      )}

      {/* Modal Vincular Plataforma */}
      {showLinkModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="glass-panel w-full max-w-lg p-6 animate-scale-in max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-white font-semibold text-lg">Vincular Plataforma</h3>
              <button onClick={() => { setShowLinkModal(false); setLinkStep(1); }} className="text-gray-400 hover:text-white">
                <X size={20} />
              </button>
            </div>

            {linkStep === 1 && (
              <>
                <p className="text-gray-400 text-sm mb-6">
                  Ingresa las 3 claves únicas de vinculación proporcionadas por la plataforma a conectar.
                </p>

                <div className="space-y-4">
                  <div>
                    <label className="block text-gray-400 text-sm mb-2">URL de la Plataforma</label>
                    <div className="flex items-center gap-2">
                      <Globe size={18} className="text-gray-500" />
                      <input
                        type="url"
                        value={linkForm.platform_url}
                        onChange={(e) => setLinkForm({ ...linkForm, platform_url: e.target.value })}
                        placeholder="https://app.ejemplo.com"
                        className="input-glass flex-1"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-gray-400 text-sm mb-2">Código Único de Plataforma</label>
                    <div className="flex items-center gap-2">
                      <Code size={18} className="text-gray-500" />
                      <input
                        type="text"
                        value={linkForm.platform_code}
                        onChange={(e) => setLinkForm({ ...linkForm, platform_code: e.target.value.toUpperCase() })}
                        placeholder="OK12341234567"
                        className="input-glass flex-1 font-mono"
                        maxLength={13}
                      />
                    </div>
                    <p className="text-gray-600 text-xs mt-1">Formato: OK + 4 dígitos (fecha) + 7 caracteres</p>
                  </div>

                  <div>
                    <label className="block text-gray-400 text-sm mb-2">API Key</label>
                    <div className="flex items-center gap-2">
                      <Key size={18} className="text-gray-500" />
                      <input
                        type="password"
                        value={linkForm.api_key}
                        onChange={(e) => setLinkForm({ ...linkForm, api_key: e.target.value })}
                        placeholder="sk-xxxxxxxxxxxxxxxx"
                        className="input-glass flex-1"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button onClick={() => setShowLinkModal(false)} className="btn-secondary flex-1">Cancelar</button>
                  <button onClick={handleDiscoverEndpoints} disabled={linking} className="btn-primary flex-1 flex items-center justify-center gap-2">
                    {linking ? <RefreshCw className="animate-spin" size={16} /> : <Bot size={16} />}
                    {linking ? "Conectando..." : "Conectar con Agente"}
                  </button>
                </div>
              </>
            )}

            {linkStep === 2 && (
              <>
                <div className="flex items-center gap-2 mb-4">
                  <Check size={20} className="text-green-400" />
                  <span className="text-green-400">Conexión establecida</span>
                </div>

                <p className="text-gray-400 text-sm mb-4">
                  Selecciona los endpoints que deseas habilitar:
                </p>

                <div className="space-y-2 max-h-60 overflow-y-auto mb-4">
                  <button 
                    onClick={() => setSelectedEndpoints(discoveredEndpoints.filter(e => e.available).map(e => e.path))}
                    className="text-red-400 text-sm hover:underline"
                  >
                    Seleccionar todos
                  </button>
                  {discoveredEndpoints.map((ep, idx) => (
                    <label key={idx} className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                      ep.available ? 'bg-black/20 hover:bg-black/30' : 'bg-black/10 opacity-50'
                    }`}>
                      <input
                        type="checkbox"
                        checked={selectedEndpoints.includes(ep.path)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedEndpoints([...selectedEndpoints, ep.path]);
                          } else {
                            setSelectedEndpoints(selectedEndpoints.filter(p => p !== ep.path));
                          }
                        }}
                        disabled={!ep.available}
                        className="w-4 h-4 accent-red-500"
                      />
                      <div className="flex-1">
                        <code className="text-gray-300 text-sm">{ep.method} {ep.path}</code>
                        {ep.requires_auth && <span className="text-yellow-400 text-xs ml-2">(Auth)</span>}
                      </div>
                      <span className={`text-xs ${ep.available ? 'text-green-400' : 'text-red-400'}`}>
                        {ep.available ? 'Disponible' : 'No disponible'}
                      </span>
                    </label>
                  ))}
                </div>

                <div className="bg-black/20 rounded-lg p-3 mb-4">
                  <p className="text-gray-400 text-sm">OAuth SSO: <span className="text-yellow-400">Pendiente de configuración</span></p>
                </div>

                <div className="flex gap-3">
                  <button onClick={() => setLinkStep(1)} className="btn-secondary flex-1">Atrás</button>
                  <button onClick={handleFinalConnect} disabled={linking} className="btn-primary flex-1 flex items-center justify-center gap-2">
                    {linking ? <RefreshCw className="animate-spin" size={16} /> : <Link2 size={16} />}
                    {linking ? "Conectando..." : "Conectar"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminConnect;
